import { memo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { PaytrieAPI } from '@paytrie/client'
import { formatMoney } from '@paytrie/common/utils'
import { formatDate } from '../dashboardDisplayUtils'
import { OtcStatusBadge } from './OtcStatusBadge'
import { trackOtcRequestOpened } from '@/utils/analytics'

type OtcRequestRowProps = {
  index: number
  request: PaytrieAPI.OtcRequest
}

export const OtcRequestRow = memo(function OtcRequestRow({
  index,
  request,
}: OtcRequestRowProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const handleOpen = useCallback(() => {
    trackOtcRequestOpened({
      otcRequestId: request.id,
      status: request.status,
      position: index,
    })
    navigate(`/otc-requests/${request.id}`)
  }, [request.id, request.status, index, navigate])

  return (
    <tr
      className="cursor-pointer"
      onClick={handleOpen}
      title={t('otcRequestRows.viewDetailsTooltip')}
      data-testid={`row-otc-request-${index}`}
    >
      <td
        className="text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 text-left break-all"
        data-testid={`cell-otc-request-${index}-date`}
      >
        {formatDate(request.createdAt)}
      </td>
      <td
        className="text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 px-2 break-all"
        data-testid={`cell-otc-request-${index}-id`}
      >
        {request.id}
      </td>
      <td
        className="text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 px-2 break-all"
        data-testid={`cell-otc-request-${index}-from`}
      >
        {formatMoney(request.leftSideValue)} {request.leftSideLabel}
      </td>
      <td
        className="text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 px-2 break-all"
        data-testid={`cell-otc-request-${index}-to`}
      >
        {request.rightSideLabel}
      </td>
      <td
        className="text-sm py-3 sm:py-6 border-b border-gray-300 dark:border-gray-700 text-center"
        data-testid={`cell-otc-request-${index}-status`}
      >
        <OtcStatusBadge
          status={request.status}
          cancelledAt={request.cancelledAt}
          testId={`indicator-otc-request-${index}-status`}
        />
      </td>
    </tr>
  )
})
